import { App, Checkbox } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { memo, useCallback, useMemo, useState } from "react";
import { cn } from "sipli-fleet-core/utils/cn";
import { readVisibleColumnsFromStorage, saveVisibleColumnsToStorage } from "../../utils/columnVisibilityStorage";
import CustomButton from "../CustomSharedButton/CustomSharedButton";
import CustomModal from "../CustomSharedModal/CustomSharedModal";
import CustomTypography from "../CustomSharedTypography/CustomSharedTypography";

type TranslateFn = (key: string) => string;

interface SelectableCheckboxRowProps {
  optionKey: string;
  label: string;
  checked: boolean;
  onToggle: (optionKey: string, isChecked: boolean) => void;
}

export interface ColumnsConfiguratorProps {
  options?: string[];
  visibleKeys?: string[];
  onSave: (visibleKeys: string[]) => void;
  t: TranslateFn;
  storageKey?: string;
  minSelected?: number;
  chartDataSetLength?: number;
  buttonLabelKey?: string;
  titleKey?: string;
  descriptionKey?: string;
}

const SelectableCheckboxRow = memo(function SelectableCheckboxRow({
  optionKey,
  label,
  checked,
  onToggle,
}: SelectableCheckboxRowProps) {
  const handleChange = useCallback(
    (e: CheckboxChangeEvent) => {
      onToggle(optionKey, e.target.checked);
    },
    [onToggle, optionKey],
  );

  return (
    <Checkbox checked={checked} onChange={handleChange}>
      <span className="text-[15px] text-black">{label}</span>
    </Checkbox>
  );
});

const ColumnsConfigurator = memo(function ColumnsConfigurator({
  options = [],
  visibleKeys = [],
  onSave,
  t,
  storageKey,
  minSelected = 1,
  chartDataSetLength = 0,
  buttonLabelKey = "configure_columns",
  titleKey = "configure_columns",
  descriptionKey = "configure_columns_description",
}: ColumnsConfiguratorProps) {
  const { message } = App.useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [draftVisibleSet, setDraftVisibleSet] = useState<Set<string>>(
    () => new Set(),
  );

  const optionLabelMap = useMemo(
    () =>
      options.reduce<Record<string, string>>((acc, option) => {
        acc[option] = t(option);
        return acc;
      }, {}),
    [options, t],
  );

  const draftVisibleKeys = useMemo(
    () => options.filter((key) => draftVisibleSet.has(key)),
    [options, draftVisibleSet],
  );

  const openModal = useCallback(() => {
    const parsed = storageKey
      ? readVisibleColumnsFromStorage(storageKey)
      : null;
    const normalized = Array.isArray(parsed)
      ? parsed.filter((key): key is string => options.includes(key))
      : [];
    const safeKeys = normalized.length ? normalized : visibleKeys;

    setDraftVisibleSet(new Set(safeKeys));

    setIsOpen(true);
  }, [storageKey, options, visibleKeys]);

  const handleToggle = useCallback(
    (optionKey: string, isChecked: boolean) => {
      setDraftVisibleSet((prevState) => {
        const nextState = new Set(prevState);

        if (isChecked) {
          nextState.add(optionKey);
          return nextState;
        }

        if (prevState.size <= minSelected) return prevState;
        nextState.delete(optionKey);
        return nextState;
      });
    },
    [minSelected],
  );

  const handleSave = useCallback(() => {
    if (draftVisibleKeys.length < minSelected) {
      message.warning(t("please_select_at_least_on_column"));
      return;
    }

    if (storageKey) saveVisibleColumnsToStorage(storageKey, draftVisibleKeys);

    onSave(draftVisibleKeys);
    setIsOpen(false);
  }, [draftVisibleKeys, minSelected, message, onSave, storageKey, t]);

  const handleReset = useCallback(() => {
    setDraftVisibleSet(new Set(options));
  }, [options]);

  const isAllSelected = draftVisibleKeys.length === options.length;

  return (
    <>
      <div
        className={cn("flex items-end h-full pb-1.5 pr-3 mt-1.5", {
          "mt-0": chartDataSetLength > 0,
        })}
      >
        <CustomButton
          type="secondary"
          onClick={openModal}
          label={t(buttonLabelKey)}
          disabled={options.length === 0}
        />
      </div>

      <CustomModal
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        onOk={handleSave}
        okText={t("save")}
        cancelText={t("cancel")}
        width={420}
        centered
        footer={
          <div className="flex justify-end gap-3 mt-4">
            <CustomButton
              label={t("cancel")}
              buttonSize="medium"
              type="secondary"
              onClick={() => setIsOpen(false)}
            />
            <CustomButton
              label={t("reset")}
              buttonSize="medium"
              type="secondary"
              onClick={handleReset}
              disabled={isAllSelected}
            />
            <CustomButton
              label={t("save")}
              buttonSize="medium"
              type="primary"
              onClick={handleSave}
            />
          </div>
        }
      >
        <div className="pt-2">
          <CustomTypography.CustomTitle className="!text-[20px] !leading-[28px] !mb-2">
            {t(titleKey)}
          </CustomTypography.CustomTitle>
          <CustomTypography.CustomText className="!text-gray9 !text-[15px]">
            {t(descriptionKey)}
          </CustomTypography.CustomText>

          <div className="flex flex-col gap-4 mt-6">
            {options.map((optionKey) => (
              <SelectableCheckboxRow
                key={optionKey}
                optionKey={optionKey}
                label={optionLabelMap[optionKey]}
                checked={draftVisibleSet.has(optionKey)}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </div>
      </CustomModal>
    </>
  );
});

export default ColumnsConfigurator;
